import {
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    KbdColorsConstantType,
    KbdSizeConstantType,
    KbdDynamicStyleType,
    KbdMeasures,
    KbdSizeKeys,
    KbdColors,
    KbdSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const KBD_SIZES: Record<KbdSize, KbdSizeKeys> = {
    medium: {
        labelVariant: "labelMediumSize",
        paddingExtra: "spacingXxs"
    },
    small: {
        labelVariant: "labelSmallSize"
    }
};

export const KBD_RADIUS_DIVISORS: Record<"standalone" | "field", number> = {
    standalone: 2,
    field: 4
};

export const getKbdSize = ({
    typography,
    isInField,
    radiuses,
    spaces,
    size
}: KbdSizeConstantType): KbdMeasures => {
    const sizeKeys = KBD_SIZES[size];

    const paddingExtra = sizeKeys.paddingExtra ? spaces[sizeKeys.paddingExtra] : 0;

    return {
        borderRadius: radiuses.form / KBD_RADIUS_DIVISORS[isInField ? "field" : "standalone"],
        minHeight: typography[sizeKeys.labelVariant].lineHeight + spaces.spacingXxs * 2,
        paddingHorizontal: spaces.spacingXs + paddingExtra,
        labelVariant: sizeKeys.labelVariant
    };
};

export const getKbdColors = ({
    isDisabled,
    isInverse,
    variant,
    colors
}: KbdColorsConstantType): KbdColors => {
    const isGhost = variant === "ghost";

    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        if(isGhost) {
            return {
                containerColor: "transparent",
                separatorColor: disabledColor,
                borderColor: "transparent",
                labelColor: disabledColor
            };
        }

        return {
            containerColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            separatorColor: disabledColor,
            labelColor: disabledColor
        };
    }

    if(isInverse) {
        return {
            separatorColor: colors.content.text.contrastLow,
            borderColor: colors.content.text.contrastLow,
            labelColor: colors.content.text.contrastMid,
            containerColor: "transparent"
        };
    }

    if(isGhost) {
        return {
            separatorColor: colors.content.text.low,
            labelColor: colors.content.text.low,
            containerColor: "transparent",
            borderColor: "transparent"
        };
    }

    return {
        containerColor: colors.system.state.overlay.fill.default,
        borderColor: colors.content.border.subtle,
        separatorColor: colors.content.text.mid,
        labelColor: colors.content.text.mid
    };
};

const NATIVE_DIRECTION_STYLE: ViewStyle = Platform.OS === "web" ? {} : {
    direction: "ltr"
};

const stylesheet = StyleSheet.create({
    hiddenName: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        ...webStyle({
            clipPath: "inset(50%)",
            whiteSpace: "nowrap"
        })
    },
    container: {
        pointerEvents: "none",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0,
        ...NATIVE_DIRECTION_STYLE,
        ...webStyle({
            writingDirection: "ltr"
        })
    },
    key: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center",
        ...webStyle({
            userSelect: "none"
        })
    },
    label: {
        textAlign: "center",
        fontVariant: [
            "tabular-nums"
        ]
    }
});

export const useStyles = ({
    currentColors,
    shapeConfigs,
    currentSize,
    borders,
    spaces
}: KbdDynamicStyleType) => {
    const styles = {
        key: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            minWidth: currentSize.minHeight,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: currentSize.borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        container: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        hiddenName: {
            height: borders.line,
            width: borders.line
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
